import React from 'react';
import { MuzzleEarBase, MuzzleEarInner } from './sections/MuzzleEarSection';
import { MuzzleBody } from './sections/MuzzleBodySection';
import { MuzzleEarButtons, MuzzleCheekButtons } from './sections/MuzzleButtonsSection';
import { MuzzleFace } from './sections/MuzzleFaceSection';
import { FaceDetailSection, MuzzleWhiskerSection, isHoodFaceDetailFamily } from './sections/FaceDetailSection';

/**
 * MUZZLE mode — muzzle-only view. Different config interpretation
 * (fewer color parts, one-size).
 *
 * Composition order below is the SVG **z-order** (later = painted on top), the same
 * order the original renderer produced. Components are named after their menu sections:
 *
 *   §4 耳朵细节      MuzzleEarBase + MuzzleEarInner (outer paints first, inners after body)
 *   (fixed)          MuzzleBody — shading, highlights, straps, seams
 *   §5 其他          MuzzleEarButtons + MuzzleCheekButtons
 *   §2 面部          MuzzleFace (鼻梁/鼻头/脸部/嘴唇)
 *   §3 脸部细节      FaceDetailSection (cross-mode, renders the hood face-detail set)
 */
const MuzzleView = ({ config, parts, lines, rects, ellipses, polygons, faceDetail, muzzleDetail }) => {
  return (
    <g id="muzzle_hood">
      {/* §4 耳朵细节 — outer (C + A/B) */}
      <MuzzleEarBase config={config} parts={parts} />

      {/* Fixed construction — body shading, highlights, straps, seams */}
      <MuzzleBody parts={parts} polygons={polygons} />

      {/* §4 耳朵细节 — inner (A/B) */}
      <MuzzleEarInner config={config} parts={parts} />

      {/* §5 其他 — 耳朵纽扣 (鼻梁纽扣 ×2) */}
      <MuzzleEarButtons config={config} parts={parts} ellipses={ellipses} />

      {/* §2 面部 — muzzle template face (鼻梁/鼻头/脸部/嘴唇). Renders ONLY for
          the muzzle family (style1/style2): for 圆脸/FUR the hood round/fur muzzle
          (FaceDetailStyles) is the face, so the muzzle-template face would be a
          duplicated layer on top of it. */}
      {!isHoodFaceDetailFamily(config['muzzle-style']) && (
        <MuzzleFace config={config} parts={parts} lines={lines} rects={rects} />
      )}

      {/* §3 脸部细节 — cross-mode: same face-detail set as hood (圆脸/FUR, 脸颊,
          三孔/胡须/皮毛, A/B, 柳丁), rendered from the hood template's artwork.
          showBridge=false: 圆脸/FUR hide the hood bridge here so they match
          style1/style2 (both show the muzzle bridge rendered below). */}
      <FaceDetailSection config={config} faceDetail={faceDetail} showBridge={false} />

      {/* §2/§3 — the muzzle bridge + its dashed dot-line seams (like style1/2 get via
          MuzzleFace), shown for 圆脸/FUR in the muzzle view */}
      {isHoodFaceDetailFamily(config['muzzle-style']) && (
        <>
          <path
            className={"hood-" + config['bridge-color'] + " str_222222_50"}
            d={muzzleDetail.parts['bridge-color_c'] || ''}
          />
          <path className="clear str_222222_25_125_75" d={muzzleDetail.parts['clear-path-32'] || ''}/>
          <path className="clear str_222222_20_40_20" d={muzzleDetail.parts['clear-path-33'] || ''}/>
        </>
      )}

      {/* §3 脸部细节 — original muzzle face details (胡子 whiskers + 鼻贴 nose patch),
          from the muzzle template's own artwork */}
      <MuzzleWhiskerSection config={config} muzzleDetail={muzzleDetail} />

      {/* §5 其他 — 脸颊纽扣（上/下） */}
      <MuzzleCheekButtons config={config} parts={parts} ellipses={ellipses} />
    </g>
  );
};

export default MuzzleView;
